import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "How does the credit system work?",
    answer: "Every AI action (extraction, Smart Categories, Smart Notes) costs credits. Basic extraction costs 1 credit, Smart Categories cost 0.5 credits per category checked, and Smart Notes cost 0.7-2 credits depending on complexity. You start with 20 free welcome credits that never expire. Credits from paid plans renew monthly, or you can buy top-ups anytime.",
  },
  {
    question: "What's the difference between Starter and Pro?",
    answer: "Starter (€5/month) includes 200 credits and basic AI extraction only. Pro (€19/month) unlocks Smart Categories, Smart Notes, Field Presets, API access, and 1,000 credits. Pro is recommended for power users who need intelligent tagging and research.",
  },
  {
    question: "Can I switch plans anytime?",
    answer: "Yes! You can upgrade or downgrade your plan at any time. Unused credits from your previous plan will carry over (up to your new plan's monthly limit).",
  },
  {
    question: "What happens if I run out of credits?",
    answer: "If you run out of credits mid-month, you can buy a credit top-up (starting at €5 for 100 credits). Top-up credits never expire and can be used across billing periods.",
  },
  {
    question: "How does Email2Contact work?",
    answer: "Email2Contact analyzes email signatures using AI-powered extraction (Azure OpenAI GPT-4o Vision). When you open an email in Outlook, click the Email2Contact button to extract contact information from the signature. Review the extracted data and save it directly to your Outlook contacts with one click.",
  },
  {
    question: "Is my data safe and privacy-focused?",
    answer: "Yes, absolutely. Email2Contact is built with GDPR principles and privacy-first architecture:\n\n• Regional Processing: AI features use EU-exclusive servers (ConvertAPI EU, Azure OpenAI West Europe) – no data leaves the EU\n• Zero Data Retention: Email signatures are processed in-memory only and immediately deleted after analysis (ephemeral processing)\n• No AI Training: Your data is never used to train AI models (guaranteed by Microsoft DPA and Google DPA)\n• Explicit Consent: AI-powered extraction requires you to click \"AI-Enhance\" – you control when AI processing occurs\n• Minimal Data Transfer: Only signature content (as PNG image) and company metadata are transmitted, never full email bodies or your inbox\n• Secure Authentication: Your Microsoft 365 credentials stay with Microsoft – we never access your password\n\nFor full details, see our Privacy Policy.",
  },
  {
    question: "What information can be extracted?",
    answer: "Email2Contact can extract names, company names, job titles, email addresses, phone numbers (mobile and business), and website URLs from email signatures. The AI-powered extraction engine (Azure OpenAI GPT-4o Vision) recognizes various signature formats and structures.",
  },
  {
    question: "Which AI services does Email2Contact use?",
    answer: "Email2Contact uses two optional AI services, both privacy-focused and EU-hosted:\n\n1. Signature Analysis (Azure OpenAI GPT-4o Vision)\n   • Purpose: Enhanced extraction of contact data from complex signatures\n   • Location: West Europe (Netherlands, Azure Data Center)\n   • Data sent: Screenshot of email signature only (as PNG image)\n   • Retention: Zero (ephemeral processing, no logging)\n\n2. Smart Notes (Lovable AI / Google Gemini 2.5 Flash)\n   • Purpose: Automated company research notes\n   • Location: Google Cloud EU regions\n   • Data sent: Company name and website only\n   • Retention: Zero (ephemeral processing)\n\nBoth services require explicit opt-in and can be disabled at any time.",
  },
  {
    question: "What data is stored in your database?",
    answer: "Email2Contact stores only your account and preference data:\n\n• User Profile: Email address, name (from Microsoft 365 login)\n• Settings: Smart Categories, Field Presets, Smart Notes tasks\n• Usage Logs: Credits consumed, feature usage (for billing purposes only)\n• Subscription: Active plan, billing status\n\nWe do NOT store:\n❌ Email content or bodies\n❌ Email signatures or screenshots\n❌ Contact data from your Outlook address book\n❌ Calendar or meeting information\n\nAll contact data is saved directly to your Outlook account and remains under your control.",
  },
  {
    question: "How do I install Email2Contact?",
    answer: "You can install Email2Contact from the Microsoft AppSource marketplace. Search for 'Email2Contact' in your Outlook add-ins section, or install it directly from the AppSource website. Once installed, it will appear in your Outlook ribbon.",
  },
  {
    question: "Does it work with Outlook Web and Desktop?",
    answer: "Yes, Email2Contact works seamlessly with both Outlook Web (Office 365) and Outlook Desktop applications. The add-in provides the same functionality across all platforms.",
  },
  {
    question: "What happens if a contact already exists?",
    answer: "Email2Contact performs a duplicate check before saving. If a contact with the same email address already exists, you'll be notified and can choose whether to create a new contact or cancel the operation.",
  },
  {
    question: "Is there a free trial?",
    answer: "Yes, Email2Contact offers a free trial period so you can test all features before committing to a subscription. No credit card required for the trial.",
  },
  {
    question: "Can I edit the extracted information before saving?",
    answer: "Absolutely! After extraction, you'll see a preview of all detected information. You can edit, add, or remove any fields before saving the contact to ensure accuracy.",
  },
  {
    question: "What languages are supported?",
    answer: "Email2Contact supports email signatures in all major languages including English, German, French, Spanish, Italian, and many more. The parser is language-agnostic and focuses on structural patterns.",
  },
  {
    question: "How accurate is the extraction?",
    answer: "The extraction accuracy depends on the signature format. For well-structured signatures, accuracy is typically 90%+ for all fields. You always have the opportunity to review and correct any information before saving.\n\nNote: The AI-powered Signature Analysis uses Azure OpenAI (West Europe, GDPR-compliant, zero retention) to analyze complex signature layouts for maximum accuracy.",
  },
  {
    question: "Do I need a Microsoft 365 subscription?",
    answer: "Email2Contact works with any Outlook account, including Microsoft 365, Outlook.com, and Exchange accounts. However, saving contacts requires appropriate permissions in your Outlook account.",
  },
  {
    question: "How do I get support?",
    answer: "You can reach our support team via the contact form on this page, or email us directly at info@celsios.io. We typically respond within 24 hours on business days.",
  },
  {
    question: "Does Email2Contact work offline?",
    answer: "No. Email2Contact requires an internet connection to process email signatures using AI-powered extraction (Azure OpenAI West Europe). All processing follows GDPR principles with zero data retention in EU-exclusive servers.",
  },
];

export const FAQ = () => {
  return (
    <Accordion type="single" collapsible className="w-full">
      {faqs.map((faq, index) => (
        <AccordionItem key={index} value={`item-${index}`}>
          <AccordionTrigger className="text-left">
            {faq.question}
          </AccordionTrigger>
          <AccordionContent className="text-muted-foreground">
            {faq.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
